import { DEFAULT_EXPORT_FILE_NAME } from "./constants";
import { formatExportValue } from "./export-utils";
import type {
  CsvExportOptions,
  CsvExportResult,
  ExportTableCell,
  ExportTableModel,
} from "./export.types";

const DEFAULT_SEPARATOR = ",";
const DEFAULT_LINE_BREAK = "\r\n";
const DEFAULT_MIME_TYPE = "text/csv;charset=utf-8";

export class CsvExportService {
  serialize(table: ExportTableModel, options: CsvExportOptions = {}): CsvExportResult {
    const separator = options.separator ?? DEFAULT_SEPARATOR;
    const lineBreak = options.lineBreak ?? DEFAULT_LINE_BREAK;
    const includeHeaders = options.includeHeaders ?? true;
    const rows: string[] = [];

    if (options.includeExcelSeparatorHint) {
      rows.push(`sep=${separator}`);
    }

    if (includeHeaders) {
      rows.push(
        table.columns.map((column) => this.serializeString(column.headerText)).join(separator)
      );
    }

    rows.push(
      ...table.rows.map((row) => row.cells.map((cell) => this.serializeCell(cell)).join(separator))
    );

    return {
      content: rows.join(lineBreak),
      fileName: options.fileName ?? `${DEFAULT_EXPORT_FILE_NAME}.csv`,
      includeBom: options.includeBom ?? false,
      mimeType: DEFAULT_MIME_TYPE,
    };
  }

  private serializeCell(cell: ExportTableCell): string {
    const rawValue = cell.formattedValue ?? cell.value;

    if (rawValue === null || rawValue === undefined) {
      return "";
    }

    if (typeof rawValue === "number" || typeof rawValue === "boolean") {
      return formatExportValue(rawValue);
    }

    return this.serializeString(formatExportValue(rawValue));
  }

  private serializeString(value: string): string {
    return `"${value.replace(/"/g, '""')}"`;
  }
}

const csvExportService = new CsvExportService();

export { csvExportService };
